
import { supabase } from "@/lib/supabase";

// Define interfaces for our return types
interface UserWhoLiked {
  id: string;
  name: string;
  profileId: string | null;
}

interface ProfileData {
  name?: string;
  user_id?: string;
  [key: string]: any;
}

// Get users who liked a specific video
export async function getUsersWhoLikedVideo(videoId: number): Promise<UserWhoLiked[]> {
  try {
    const { data, error } = await supabase
      .from('likes')
      .select(`
        user_id,
        profiles:profile(
          name,
          user_id
        )
      `)
      .eq('video_id', videoId);

    if (error) {
      console.error('Error fetching users who liked video:', error);
      return [];
    }

    if (!data) return [];

    // Transform the data to return user information
    const users = data.map(like => {
      // Handle possible null values and type safety
      const profile: ProfileData = like.profiles || {};
      
      // Handle name which could be a string or JSON object
      let displayName = "Unknown User";
      if (profile.name) {
        if (typeof profile.name === 'string') {
          displayName = profile.name;
        } else if (typeof profile.name === 'object') {
          // If name is stored as JSON, stringify it
          displayName = JSON.stringify(profile.name);
        }
      }
      
      return {
        id: like.user_id || '',
        name: displayName,
        profileId: profile.user_id || null
      };
    });

    return users;
  } catch (error) {
    console.error('Error fetching users who liked video:', error);
    return [];
  }
}

// Like a video
export async function likeVideo(userId: string, videoId: number) {
  try {
    const { data, error } = await supabase
      .from('likes')
      .insert([{ user_id: userId, video_id: videoId }]);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error liking video:', error);
    return { success: false, error };
  }
}

// Unlike a video
export async function unlikeVideo(userId: string, videoId: number) {
  try {
    const { data, error } = await supabase
      .from('likes')
      .delete()
      .eq('user_id', userId)
      .eq('video_id', videoId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error unliking video:', error);
    return { success: false, error };
  }
}

// Check if user has liked a specific video
export async function hasUserLikedVideo(userId: string, videoId: number): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('likes')
      .select('id')
      .eq('user_id', userId)
      .eq('video_id', videoId)
      .maybeSingle();

    if (error) {
      console.error('Error checking if user liked video:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Error checking if user liked video:', error);
    return false;
  }
}

// Get count of likes for a video
export async function getVideoLikesCount(videoId: number): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('likes')
      .select('id', { count: 'exact' })
      .eq('video_id', videoId);

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error counting video likes:', error);
    return 0;
  }
}

// Toggle like status (like if not liked, unlike if already liked)
export async function toggleLike(userId: string, videoId: number) {
  try {
    const isLiked = await hasUserLikedVideo(userId, videoId);
    
    if (isLiked) {
      await unlikeVideo(userId, videoId);
      return false;
    } else {
      await likeVideo(userId, videoId);
      return true;
    }
  } catch (error) {
    console.error('Error toggling like status:', error);
    return false;
  }
}

// Get likes count - helper function for VideoPlayer component
export async function getLikesCount(videoId: number): Promise<number> {
  return getVideoLikesCount(videoId);
}
